import { useState } from 'react';
import { Datagrid } from 'datagrid';
import type {
  TableCellRenderers,
  TablePresentationOptions,
  TableRowClassNameResolver,
} from 'datagrid';
import { SiteNav } from '../components/SiteNav';
import { SiteFooter } from '../components/SiteFooter';
import { orderMailto } from '../lib/orderMailto';

const footerLinks = [
  { label: 'GitHub', href: 'https://github.com/vibelibs/datagrid' },
  { label: 'npm', href: 'https://www.npmjs.com/package/@vibelibs/datagrid' },
  { label: 'Docs', href: 'https://github.com/vibelibs/datagrid#readme' },
  { label: 'Contact', href: 'mailto:hello@vibelibs.dev' },
];

type InstallPkg = 'npm' | 'pnpm' | 'bun' | 'yarn';

const installCmds: Record<InstallPkg, string> = {
  npm:  'npm install @vibelibs/datagrid',
  pnpm: 'pnpm add @vibelibs/datagrid',
  bun:  'bun add @vibelibs/datagrid',
  yarn: 'yarn add @vibelibs/datagrid',
};

const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'Demo',     href: '#demo' },
  { label: 'Pricing',  href: '#pricing' },
  { label: 'Docs',     href: 'https://github.com/vibelibs/datagrid#readme' },
];

const features = [
  {
    icon: '⚡',
    color: 'green' as const,
    name: 'Real-time by default',
    desc: 'WebSocket data streams built into the provider layer. Your table stays live without a single line of polling code.',
  },
  {
    icon: '⌘',
    color: 'purple' as const,
    name: 'Command Palette',
    desc: 'Hit ⌘K and search columns, jump to rows, trigger actions. Power-user UX without the power-user setup time.',
  },
  {
    icon: '◎',
    color: 'purple' as const,
    name: 'Graph Explorer',
    desc: 'Visualize schema relationships and data lineage as an interactive node graph. See the structure behind the rows.',
  },
  {
    icon: '✦',
    color: 'green' as const,
    name: 'Quality Gates',
    desc: 'Column-level data quality rules with triage views and conflict detection. Ship governance baked into the component.',
  },
  {
    icon: '▤',
    color: 'amber' as const,
    name: 'Tables Workspace',
    desc: 'Host multiple datasets in one unified shell with tabbed navigation, split views, and cross-table operations.',
  },
  {
    icon: '◑',
    color: 'green' as const,
    name: 'Statistics & Charts',
    desc: 'Bar, histogram, and correlation charts embedded in the panel layer. No extra chart lib wiring required.',
  },
  {
    icon: '◈',
    color: 'amber' as const,
    name: 'Column Watch',
    desc: 'Pin columns for monitoring. Get notifications when values change, thresholds breach, or new conflicts appear.',
  },
  {
    icon: '◻',
    color: 'purple' as const,
    name: 'TypeScript-first',
    desc: 'Strict types end to end. Full IntelliSense on every prop, hook, and provider. No any-escape hatches needed.',
  },
  {
    icon: '◎',
    color: 'red' as const,
    name: 'Onboarding Tour',
    desc: 'Ship a guided first-run experience with zero extra setup. The tour engine is part of the component package.',
  },
  {
    icon: '◉',
    color: 'purple' as const,
    name: 'MCP Server',
    desc: 'AI coding tools introspect the datagrid API natively. No hallucinated prop names, no stale docs. Correct code on the first prompt.',
  },
  {
    icon: '⊹',
    color: 'green' as const,
    name: 'Claude Code Skill',
    desc: 'Ship a /datagrid skill with your project. Scaffold, debug, and extend datagrid usage from the terminal with zero extra setup.',
  },
  {
    icon: '◰',
    color: 'amber' as const,
    name: 'Auth Adapter',
    desc: 'Plug in SSO, OIDC, or SAML in one interface. Column-level RBAC and automatic token refresh for enterprise compliance.',
  },
];

type DemoRow = {
  rowid: number;
  name: string;
  rows: string;
  updated: string;
  quality: 'Pass' | 'Warn' | 'Fail';
  owner: string;
};

const demoColumns = ['name', 'rows', 'quality', 'owner', 'updated'];

const demoRows: DemoRow[] = [
  { rowid: 1, name: 'user_events',      rows: '2.4M',  updated: '2s ago',  quality: 'Pass', owner: 'analytics' },
  { rowid: 2, name: 'product_catalog',  rows: '18.2K', updated: '12s ago', quality: 'Warn', owner: 'commerce' },
  { rowid: 3, name: 'payment_ledger',   rows: '902K',  updated: 'live',    quality: 'Pass', owner: 'finance' },
  { rowid: 4, name: 'customer_profile', rows: '5.1M',  updated: '1m ago',  quality: 'Fail', owner: 'crm' },
  { rowid: 5, name: 'audit_log',        rows: '44.7M', updated: '3s ago',  quality: 'Pass', owner: 'platform' },
];

const demoCellRenderers: TableCellRenderers = {
  name: ({ value }) => <span className="dg-live-name">{String(value)}</span>,
  rows: ({ value }) => <span className="dg-live-mono">{String(value)}</span>,
  quality: ({ value }) => (
    <span className={`dg-badge ${value === 'Pass' ? 'dg-badge-green' : value === 'Warn' ? 'dg-badge-amber' : 'dg-badge-red'}`}>
      {String(value)}
    </span>
  ),
  updated: ({ value }) => (
    <span className="dg-live-updated">
      {value === 'live' && <span className="dg-pulse" />}
      {String(value)}
    </span>
  ),
};

const demoRowClassName: TableRowClassNameResolver = ({ row }) => (
  row.name === 'payment_ledger' ? 'is-selected' : ''
);

const demoPresentation: TablePresentationOptions = {
  showHeaderInfoBar: false,
  showFooter: false,
  enableHorizontalScroll: false,
  rowHeight: 36,
  columnWidths: {
    name: 154,
    rows: 90,
    quality: 92,
    owner: 104,
    updated: 88,
  },
  headerDisplay: {
    showColumnMenu: false,
    showSortControls: true,
    sortControlVisibility: 'always',
    showFilterControls: false,
    showSamplingControls: false,
    showStatisticsControls: false,
    showDatatypeIcons: false,
    showMissingDataIndicator: false,
  },
  typography: {
    table: {
      fontFamily: 'Space Grotesk, ui-sans-serif, system-ui, -apple-system, sans-serif',
      letterSpacing: '0',
    },
    header: {
      fontSize: '0.72rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--faint)',
    },
    body: {
      fontSize: '0.8rem',
      lineHeight: 1.25,
      fontWeight: 400,
      letterSpacing: '0',
      color: 'var(--muted)',
    },
  },
  columnLabels: {
    name: 'Table name',
    rows: 'Rows',
    quality: 'Quality',
    owner: 'Owner',
    updated: 'Updated',
  },
  rowClassName: demoRowClassName,
  cellRenderers: demoCellRenderers,
  cellText: {
    name: { mode: 'truncate', maxLines: 1 },
    rows: { mode: 'truncate', maxLines: 1 },
    quality: { mode: 'truncate', maxLines: 1 },
    owner: { mode: 'truncate', maxLines: 1 },
    updated: { mode: 'truncate', maxLines: 1 },
  },
};

const faqItems = [
  {
    q: 'Is this a drop-in replacement for react-table or AG Grid?',
    a: 'It\'s more opinionated than react-table and much lighter than AG Grid. It comes with a full shell, live provider, command palette, and graph explorer pre-wired. If you want just a headless table hook, look elsewhere. If you want a production-ready data UI, this is it.',
  },
  {
    q: 'What does "vibe coding" mean for a datagrid?',
    a: 'It means you describe what you want — a live table, sortable columns, quality gates — and the component handles the plumbing. Designed to work with AI-assisted development workflows where you want to ship UI fast without fighting a library.',
  },
  {
    q: 'Is the free Community tier really free, with no strings attached?',
    a: 'Yes. The core <code>&lt;Datagrid&gt;</code> component is MIT-licensed and free forever. No license key, no usage limits, no telemetry. The shell, MCP server, Claude Code skill, and auth adapter are Pro and Enterprise features — they take ongoing maintenance and that\'s what you pay for.',
  },
  {
    q: 'What is the MCP server and does it cost anything to run?',
    a: 'An MCP (Model Context Protocol) server lets AI coding tools — Claude Code, Cursor, Copilot — call structured tools that return your exact datagrid API: prop schemas, usage examples, config validators. Instead of guessing prop names from training data, your AI assistant calls <code>get_datagrid_props</code> and gets the right answer. It runs as a local Node.js process on your machine — no cloud account, no server, no hosting cost. Install it from npm, add one line to your MCP config, and every AI tool you use will know the vibelibs API exactly.',
  },
  {
    q: 'What is the Claude Code skill?',
    a: 'A Claude Code skill is a markdown file that extends the Claude Code CLI with domain-specific commands. The vibelibs skill adds <code>/datagrid scaffold</code>, <code>/datagrid add-column</code>, <code>/datagrid debug</code>, and <code>/datagrid migrate</code>. It ships with the Pro package — drop it into your project\'s <code>.claude/</code> folder. Cost to run: zero. It\'s a file, not a service.',
  },
  {
    q: 'Can I use it without the shell / TablesWorkspace?',
    a: 'Yes. The core <code>Datagrid</code> component is fully standalone. Import just what you need. The shell exports live under <code>@vibelibs/datagrid/shell</code> so they never inflate your bundle unless you explicitly use them.',
  },
  {
    q: 'What does the Auth Adapter do and who needs it?',
    a: 'The auth adapter is a pluggable interface for enterprise teams that need SSO (Okta, Azure AD, Auth0), column-level access control based on user roles, automatic token refresh for long-running sessions, and structured audit logs of who viewed or exported which data. If you\'re building an internal tool for a company with an identity provider, this is what makes vibelibs fit their security requirements without custom auth plumbing. If you\'re an indie hacker building a SaaS product, you probably don\'t need it yet.',
  },
  {
    q: 'Does it need any backend?',
    a: 'No. It works with any data source: local arrays, REST responses, or WebSocket streams. The provider interface is generic — you write the adapter, the component owns the UI.',
  },
  {
    q: 'What\'s included in a paid license?',
    a: 'A commercial license covers unlimited projects for the developer count in your tier. You can ship to production, SaaS, and client work. You own the built output entirely. Pro is a one-time payment with one year of updates. Enterprise is annual and includes SSO, audit, and custom SLA.',
  },
];

export function DatagridPage() {
  const [activePkg, setActivePkg] = useState<InstallPkg>('pnpm');
  const [copied, setCopied] = useState(false);

  function copyInstall() {
    void navigator.clipboard.writeText(installCmds[activePkg]).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <div className="site">
      {/* ── Nav ───────────────────────────────────────── */}
      <SiteNav product="datagrid" links={navLinks} ctaHref="#pricing" ctaLabel="Get a license" />

      <main>
        {/* ── Hero ──────────────────────────────────────── */}
        <section className="hero" id="home">
          <div className="wrap">
            <div className="hero-grid">
              <div>
                <div className="hero-eyebrow">
                  <span className="hero-eyebrow-dot" />
                  @vibelibs/datagrid v1.0
                </div>
                <h1 className="hero-title">
                  The datagrid that <em>keeps up.</em>
                </h1>
                <p className="hero-lead">
                  Real-time data. Command palette. Graph explorer. Quality gates.
                  MCP-native for AI assistants. One install and your data UI is
                  production-ready. Hand-crafted for developers who ship fast.
                </p>

                <div className="install-tabs" role="tablist">
                  {(Object.keys(installCmds) as InstallPkg[]).map((pkg) => (
                    <button
                      key={pkg}
                      type="button"
                      role="tab"
                      aria-selected={activePkg === pkg}
                      className={`install-tab${activePkg === pkg ? ' is-active' : ''}`}
                      onClick={() => setActivePkg(pkg)}
                    >
                      {pkg}
                    </button>
                  ))}
                </div>

                <div className="hero-install">
                  <span className="hero-install-prompt">$</span>
                  <span>{installCmds[activePkg]}</span>
                  <button
                    type="button"
                    className={`hero-install-copy${copied ? ' copied-text' : ''}`}
                    onClick={copyInstall}
                    aria-label="Copy install command"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>

                <div className="hero-actions" style={{ marginTop: '32px' }}>
                  <a className="btn-primary" href="#demo">See it live</a>
                  <a
                    className="btn-ghost"
                    href="https://github.com/vibelibs/datagrid"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    GitHub ↗
                  </a>
                </div>
              </div>

              {/* ── Live datagrid preview ─────────────────── */}
              <div className="datagrid-preview" aria-label="Datagrid component preview" id="demo">
                <div className="dg-toolbar">
                  <span className="dg-dot dg-dot-r" />
                  <span className="dg-dot dg-dot-y" />
                  <span className="dg-dot dg-dot-g" />
                  <span style={{ marginLeft: '8px', fontSize: '0.72rem', fontFamily: 'JetBrains Mono, monospace', color: 'var(--faint)' }}>
                    TablesWorkspace
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--faint)' }}>⌘K</span>
                </div>
                <div className="dg-tab-row">
                  <span className="dg-tab is-active">Tables</span>
                  <span className="dg-tab">Graph</span>
                  <span className="dg-tab">Stats</span>
                  <span className="dg-tab">Gates</span>
                </div>
                <div className="dg-search-bar">
                  <span className="dg-search-pill">owner: analytics</span>
                  <span className="dg-search-pill purple">quality: pass</span>
                  <span className="dg-search-pill amber">updated: live</span>
                </div>
                <div className="dg-live-grid">
                  <Datagrid
                    idField="rowid"
                    columns={demoColumns}
                    data={demoRows}
                    page={1}
                    pageSize={demoRows.length}
                    pageSizeOptions={[demoRows.length]}
                    totalRows={demoRows.length}
                    totalPages={1}
                    layoutOptions={{ heightMode: 'content' }}
                    presentation={demoPresentation}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Stats bar ─────────────────────────────────── */}
        <div className="wrap">
          <div className="stats-row">
            <div className="stat-cell">
              <div className="stat-number">9</div>
              <div className="stat-label">exported components</div>
            </div>
            <div className="stat-cell">
              <div className="stat-number">4</div>
              <div className="stat-label">shell entry points</div>
            </div>
            <div className="stat-cell">
              <div className="stat-number">0</div>
              <div className="stat-label">mandatory backends</div>
            </div>
            <div className="stat-cell">
              <div className="stat-number">100%</div>
              <div className="stat-label">TypeScript</div>
            </div>
          </div>
        </div>

        {/* ── Features ──────────────────────────────────── */}
        <section className="section" id="features">
          <div className="wrap">
            <p className="section-label">What you get</p>
            <h2 className="section-title">Every feature you would have built anyway.</h2>
            <p className="section-sub" style={{ marginBottom: '48px' }}>
              Stop stitching together a datagrid, a chart library, a command menu, and a WebSocket
              adapter. It ships as one coherent package with one design language.
            </p>
            <div className="features-grid">
              {features.map((f) => (
                <article key={f.name} className="feature-cell">
                  <div className={`feature-icon ${f.color}`}>{f.icon}</div>
                  <div className="feature-name">{f.name}</div>
                  <div className="feature-desc">{f.desc}</div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── Code example ──────────────────────────────── */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <p className="section-label">Drop-in ready</p>
            <h2 className="section-title">From install to production in minutes.</h2>
            <p className="section-sub" style={{ marginBottom: '48px' }}>
              The API is small by design. One component, one provider, one config object.
              Everything else is opt-in.
            </p>
            <div className="code-layout">
              <div>
                <div className="code-block">
                  <div className="code-block-header">
                    <span className="dg-dot dg-dot-r" />
                    <span className="dg-dot dg-dot-y" />
                    <span className="dg-dot dg-dot-g" />
                    <span className="code-block-filename" style={{ marginLeft: '6px' }}>DataView.tsx</span>
                    <span className="code-block-lang">tsx</span>
                  </div>
                  <pre>{
`<span class="kw">import</span> <span class="ink">{ datagrid as Datagrid, DatagridProviderBoundary }</span>
  <span class="kw">from</span> <span class="str">'@vibelibs/datagrid'</span><span class="ink">;</span>
<span class="kw">import</span> <span class="ink">{ myProvider }</span> <span class="kw">from</span> <span class="str">'./provider'</span><span class="ink">;</span>

<span class="kw">export function</span> <span class="fn">DataView</span><span class="ink">() {</span>
  <span class="kw">return</span> <span class="ink">(</span>
    <span class="tag">&lt;DatagridProviderBoundary</span> <span class="attr">provider</span><span class="ink">=</span><span class="val">{myProvider}</span><span class="tag">&gt;</span>
      <span class="tag">&lt;Datagrid</span>
        <span class="attr">columns</span><span class="ink">=</span><span class="val">{columns}</span>
        <span class="attr">liveUrl</span><span class="ink">=</span><span class="val">"wss://api.example.com/stream"</span>
        <span class="attr">commandPalette</span>
        <span class="attr">qualityGates</span><span class="ink">=</span><span class="val">{gateConfig}</span>
      <span class="tag">/&gt;</span>
    <span class="tag">&lt;/DatagridProviderBoundary&gt;</span>
  <span class="ink">);</span>
<span class="ink">}</span>`
                  }</pre>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div className="code-block">
                  <div className="code-block-header">
                    <span className="dg-dot dg-dot-r" />
                    <span className="dg-dot dg-dot-y" />
                    <span className="dg-dot dg-dot-g" />
                    <span className="code-block-filename" style={{ marginLeft: '6px' }}>Shell usage</span>
                    <span className="code-block-lang">tsx</span>
                  </div>
                  <pre>{
`<span class="cm">// Full workspace shell</span>
<span class="kw">import</span> <span class="ink">{ TablesWorkspace }</span>
  <span class="kw">from</span> <span class="str">'@vibelibs/datagrid/shell'</span><span class="ink">;</span>

<span class="cm">// Graph view</span>
<span class="kw">import</span> <span class="ink">{ GraphExplorer }</span>
  <span class="kw">from</span> <span class="str">'@vibelibs/datagrid'</span><span class="ink">;</span>

<span class="cm">// Toast anywhere</span>
<span class="kw">import</span> <span class="ink">{ notifyToast }</span>
  <span class="kw">from</span> <span class="str">'@vibelibs/datagrid'</span><span class="ink">;</span>`
                  }</pre>
                </div>
                <div className="showcase-mini-grid">
                  <div className="showcase-mini-cell">
                    <strong>Tree-shakeable</strong>
                    <span>Import only what renders</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>ESM + CJS</strong>
                    <span>Works in every bundler</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>React 17+</strong>
                    <span>No forced upgrades</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Showcase ──────────────────────────────────── */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <p className="section-label">Deep dives</p>
            <h2 className="section-title">Three features worth knowing by name.</h2>
            <p className="section-sub" style={{ marginBottom: '48px' }}>
              Most datagrids stop at sort and filter. Here's what happens when the component
              owns more of the product surface.
            </p>
            <div className="showcase-grid">
              <div className="showcase-card featured">
                <div style={{ flex: 1 }}>
                  <span className="showcase-badge">⚡ Real-time</span>
                  <h3 className="showcase-title" style={{ marginTop: '16px' }}>
                    Live data is not a plugin. It's the default.
                  </h3>
                  <p className="showcase-body">
                    Most datagrids treat WebSocket data as an afterthought — a third-party adapter
                    you bolt on and debug forever. In <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.82rem', color: 'var(--green)', background: 'var(--surface-3)', padding: '1px 5px', borderRadius: '4px' }}>@vibelibs/datagrid</code>,
                    the live provider is part of the core architecture. Pass a WebSocket URL and the
                    component handles reconnection, partial updates, row diff, and scroll preservation
                    without a single extra hook.
                  </p>
                  <p className="showcase-body" style={{ marginTop: '12px' }}>
                    Paired with the notification center and column watch, your users get real-time
                    alerts when data they care about changes — without you writing any alert logic.
                  </p>
                </div>
                <div style={{ flexShrink: 0, width: '280px' }}>
                  <div className="code-block">
                    <div className="code-block-header">
                      <span className="hero-eyebrow-dot" style={{ width: '7px', height: '7px' }} />
                      <span className="code-block-filename">Live provider</span>
                    </div>
                    <pre style={{ fontSize: '0.78rem' }}>{
`<span class="attr">liveUrl</span><span class="ink">=</span><span class="str">"wss://..."</span>
<span class="attr">onRowChange</span><span class="ink">={</span><span class="fn">handler</span><span class="ink">}</span>
<span class="attr">reconnect</span>        <span class="cm">// auto</span>
<span class="attr">diffStrategy</span><span class="ink">=</span><span class="str">"id"</span>
<span class="attr">columnWatch</span><span class="ink">={[</span><span class="str">"status"</span><span class="ink">]}</span>`
                    }</pre>
                  </div>
                </div>
              </div>

              <div className="showcase-card">
                <span className="showcase-badge purple">⌘ Command palette</span>
                <h3 className="showcase-title">Your users shouldn't have to click.</h3>
                <p className="showcase-body">
                  The built-in command palette surfaces columns, filters, actions, and navigation
                  targets behind a single keyboard shortcut. Works out of the box. Extend it with
                  your own commands in three lines.
                </p>
                <div className="code-block" style={{ marginTop: 'auto' }}>
                  <div className="code-block-header">
                    <span className="code-block-filename">Custom command</span>
                  </div>
                  <pre style={{ fontSize: '0.78rem' }}>{
`<span class="tag">&lt;Datagrid</span>
  <span class="attr">commandPalette</span>
  <span class="attr">commands</span><span class="ink">={[{</span>
    <span class="attr">id</span><span class="ink">:</span> <span class="str">'export-csv'</span><span class="ink">,</span>
    <span class="attr">label</span><span class="ink">:</span> <span class="str">'Export CSV'</span><span class="ink">,</span>
    <span class="attr">run</span><span class="ink">:</span> <span class="fn">exportToCSV</span><span class="ink">,</span>
  <span class="ink">}]}</span>
<span class="tag">/&gt;</span>`
                  }</pre>
                </div>
              </div>

              <div className="showcase-card">
                <span className="showcase-badge purple">◎ Graph explorer</span>
                <h3 className="showcase-title">See the structure behind the rows.</h3>
                <p className="showcase-body">
                  Switch to graph view and your data becomes a node graph — entities, relationships,
                  lineage. Built on reagraph. Scope modes, edge metadata, and type colouring are
                  all configurable through a single props interface.
                </p>
                <div className="showcase-mini-grid" style={{ marginTop: 'auto' }}>
                  <div className="showcase-mini-cell">
                    <strong>Nodes</strong>
                    <span>Entities + types</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>Edges</strong>
                    <span>FK + lineage</span>
                  </div>
                  <div className="showcase-mini-cell">
                    <strong>Scope</strong>
                    <span>Focus mode</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Pricing ───────────────────────────────────── */}
        <section className="section" id="pricing">
          <div className="wrap">
            <p className="section-label">Pricing</p>
            <h2 className="section-title">Start free. Pay for the AI layer.</h2>
            <p className="section-sub" style={{ marginBottom: '56px' }}>
              The core datagrid is MIT and free forever. Pro adds the shell, MCP server, and
              Claude Code skill — the layer that makes AI assistants actually know your API.
              Enterprise adds auth, SSO, and compliance features for teams with real security needs.
            </p>
            <div className="pricing-grid">
              {/* Community — free */}
              <div className="pricing-card">
                <div className="pricing-tier">Community</div>
                <div className="pricing-price">
                  <span className="pricing-currency" style={{ fontSize: '1.1rem', alignSelf: 'flex-start', marginTop: '6px' }}></span>
                  <span className="pricing-amount">Free</span>
                  <span className="pricing-period">MIT license</span>
                </div>
                <div className="pricing-desc">
                  The core datagrid for any project. No license key required.
                </div>
                <div className="pricing-divider" />
                <ul className="pricing-features">
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Core <code style={{ fontSize: '0.8rem' }}>&lt;Datagrid&gt;</code> component
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Sort, filter, pagination, renderers
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    All TypeScript types and hooks
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Community GitHub issues
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check muted">—</span>
                    <span style={{ color: 'var(--faint)' }}>Shell &amp; workspace</span>
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check muted">—</span>
                    <span style={{ color: 'var(--faint)' }}>MCP server + AI skill</span>
                  </li>
                </ul>
                <a
                  className="pricing-btn"
                  href="https://github.com/vibelibs/datagrid"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get on GitHub
                </a>
              </div>

              {/* Pro — featured */}
              <div className="pricing-card featured">
                <div className="pricing-featured-tag">For indie hackers</div>
                <div className="pricing-tier">Pro</div>
                <div className="pricing-price">
                  <span className="pricing-currency">$</span>
                  <span className="pricing-amount">149</span>
                  <span className="pricing-period">one-time</span>
                </div>
                <div className="pricing-desc">
                  Full suite plus the AI-native layer. One payment, unlimited projects.
                </div>
                <div className="pricing-divider" />
                <ul className="pricing-features">
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Everything in Community
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Full shell: TablesWorkspace, Graph, Stats
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    MCP server — AI tools know your API
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Claude Code skill — <code style={{ fontSize: '0.8rem' }}>/datagrid</code> in your terminal
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Priority API docs (versioned schema)
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Priority support · 48h SLA
                  </li>
                </ul>
                <a
                  className="pricing-btn primary"
                  href={orderMailto({ product: '@vibelibs/datagrid', tier: 'Pro', price: '$149 one-time' })}
                >
                  Buy Pro
                </a>
              </div>

              {/* Enterprise */}
              <div className="pricing-card">
                <div className="pricing-tier">Enterprise</div>
                <div className="pricing-price">
                  <span className="pricing-currency">$</span>
                  <span className="pricing-amount">799</span>
                  <span className="pricing-period">/year</span>
                </div>
                <div className="pricing-desc">
                  For teams with SSO, audit requirements, or compliance obligations.
                </div>
                <div className="pricing-divider" />
                <ul className="pricing-features">
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Everything in Pro
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Auth adapter: SSO, OIDC, SAML
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Column-level RBAC + audit log
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Unlimited developers
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Custom SLA + dedicated support
                  </li>
                  <li className="pricing-feature">
                    <span className="pricing-check">✓</span>
                    Invoice billing available
                  </li>
                </ul>
                <a
                  className="pricing-btn"
                  href={orderMailto({ product: '@vibelibs/datagrid', tier: 'Enterprise', price: '$799/year' })}
                >
                  Contact us
                </a>
              </div>
            </div>

            <p style={{ textAlign: 'center', marginTop: '32px', fontSize: '0.85rem', color: 'var(--muted)' }}>
              Not sure which tier fits?{' '}
              <a href="mailto:hello@vibelibs.dev" style={{ color: 'var(--green)' }}>
                Write us
              </a>
              {' '}and we'll figure it out.
            </p>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────── */}
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="wrap">
            <p className="section-label">FAQ</p>
            <h2 className="section-title">Questions we get.</h2>
            <div className="faq-grid" style={{ marginTop: '40px' }}>
              {faqItems.map((item) => (
                <div key={item.q} className="faq-item">
                  <div className="faq-q">{item.q}</div>
                  <div
                    className="faq-a"
                    dangerouslySetInnerHTML={{ __html: item.a }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Banner CTA ────────────────────────────────── */}
        <div className="wrap">
          <div className="banner">
            <h2>Stop rebuilding the same data UI.</h2>
            <p>
              Your users need live updates, keyboard shortcuts, and graphs.
              Your deadline doesn't have time for you to build all three from scratch.
            </p>
            <div className="banner-actions">
              <a className="btn-primary" href="#pricing">Get a license →</a>
              <a
                className="btn-ghost"
                href="https://github.com/vibelibs/datagrid"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read the source
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* ── Footer ────────────────────────────────────── */}
      <SiteFooter links={footerLinks} />
    </div>
  );
}
